import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import _ from "lodash";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { InputProcessorType } from "~app/common/components/input/input-processor";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { SkogsvardAtgardsTyp, SkogsvardForyngringssatt } from "~app/common/models/constants";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { AtgardSkogsvardTradslag } from "~app/features/virkeskop/models/skogsvard/skogsvard-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { SkogsvardForyngringService } from "~app/features/virkeskop/services/skogsvard-foryngring.service";
import { SkogsvardService } from "~app/features/virkeskop/services/skogsvard.service";
import { canEdit } from "~app/role-permissions";
import { SkogsvardDetaljerMbForComponent } from "./skogsvard-detaljer-mb-for/skogsvard-detaljer-mb-for.component";
@Component({
    selector: "rask-skogsvard-detaljer-mb",
    templateUrl: "./skogsvard-detaljer-mb.component.html",
    styleUrls: ["./skogsvard-detaljer-mb.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SimpleSelectorComponent,
        FormsModule,
        InputNumberComponent,
        SkogsvardDetaljerMbForComponent
    ]
})
export class SkogsvardDetaljerMbComponent implements OnInit, OnDestroy {
    protected foryngringssattItems: SimpleSelectionItem[] = [];
    protected kontraktReadonly = false;
    protected malPlantorHa: number;
    protected skogsvardsAtgard: Atgard;
    protected numberDecimalProcessor = InputProcessorType.numberDecimalProcessor(2, true);

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardForyngringService = inject(SkogsvardForyngringService);
    private readonly skogsvardService = inject(SkogsvardService);

    private foryngringTradslag: AtgardSkogsvardTradslag[] = [];
    private foryngringssatt: string[] = [];

    private readonly canEdit = canEdit();

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.atgardMediatorService.atgard$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(x => {
                this.skogsvardsAtgard = x;
            });

        this.skogsvardService.getForyngringssatt()
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(x => {
                this.foryngringssatt = x;
                this.filterForyngringssatt();
            });

        this.atgardMediatorService.atgardUpdate$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(atgard => {
                this.skogsvardsAtgard = atgard;
                this.filterForyngringssatt();
                this.handleAtgardChange();
            });

        this.skogsvardForyngringService.skogsvardForyngringTradslag$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(f => {
                this.foryngringTradslag = f;
                this.setMalPlantorHa();
            });

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngDestroyed$))
            .subscribe(readonly => this.kontraktReadonly = readonly);
    }

    protected canEditMalPlantorHa(): boolean {
        return !this.isMarkberedningSadd();
    }

    protected isMarkberedningDikesrensning(): boolean {
        return this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.dikesrensning;
    }

    protected isMarkberedningSadd(): boolean {
        return this.skogsvardsAtgard?.atgardstyp === SkogsvardAtgardsTyp.markberedningSadd;
    }

    protected onForyngringssattChanged(foryngringssatt: string): void {
        if (!_.isNil(foryngringssatt)) {
            this.update();
        }
    }

    protected onMalPlantorHaChange($event): void {
        if (!_.isNil($event) && !this.isMarkberedningSadd()) {
            this.update();
        }
    }

    protected onNettoarealChanged($event): void {
        if (!_.isNil($event)) {
            this.kontraktMediatorService.updateNettoarealSrc.next({
                atgardGuid: this.skogsvardsAtgard.guid,
                nettoareal: this.skogsvardsAtgard.nettoareal,
                atgardstyp: this.skogsvardsAtgard.atgardstyp,
                avverkningsanmalanGodkantDatum: null,
                beteckning: this.skogsvardsAtgard.beteckning
            });

            this.update();
        }
    }

    protected showForyngring(): boolean {
        return this.isMarkberedningSadd();
    }

    private filterForyngringssatt(): void {
        this.foryngringssattItems = [];
        let foryngring: string[];

        if (this.isMarkberedningSadd()) {
            foryngring = this.foryngringssatt.filter(x => x === SkogsvardForyngringssatt.sadd);
        } else if (this.isMarkberedningDikesrensning()) {
            foryngring = [];
        } else {
            foryngring = this.foryngringssatt.filter(x => x !== SkogsvardForyngringssatt.sadd);
        }

        foryngring.forEach(x => {
            this.foryngringssattItems.push({
                value: x,
                text: x
            });
        });
    }

    private handleAtgardChange(): void {
        if (this.isMarkberedningSadd()) {
            this.skogsvardsAtgard.foryngringssatt = SkogsvardForyngringssatt.sadd;
        } else {
            if (this.skogsvardsAtgard.foryngringssatt === SkogsvardForyngringssatt.sadd) {
                this.skogsvardsAtgard.foryngringssatt = null;
            }
        }
    }

    private setMalPlantorHa(): void {
        if (this.isMarkberedningSadd()) {
            const malFrogiva = this.skogsvardForyngringService.getMalFrogiva(this.foryngringTradslag);
            this.skogsvardsAtgard.malFrogivaHa = malFrogiva === 0 ? null : malFrogiva;
        }
    }

    private update(): void {
        this.atgardMediatorService.update(this.skogsvardsAtgard);
    }
}
